/**
 * Data Processing Agreement Page
 * Legal compliance page for DPDP Act 2023
 */

export default function DataProcessingAgreementPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Data Processing Agreement</h1>
            <p className="text-gray-600 mt-2">Compliance with Digital Personal Data Protection Act 2023</p>
          </div>

          <div className="prose max-w-none">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              This Data Processing Agreement ("Agreement") governs the processing of personal data by PureAum in accordance with the Digital Personal Data Protection Act, 2023 ("DPDP Act") and other applicable data protection laws in India.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Data We Collect</h2>
            <div className="text-gray-700 mb-4">
              <p className="mb-2">We collect and process the following categories of personal data:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Business registration information (company name, registration numbers, addresses)</li>
                <li>Contact information (email addresses, phone numbers)</li>
                <li>Authorized signatory details (name, designation, contact information)</li>
                <li>Banking and financial information for payment processing</li>
                <li>Tax identification numbers (GSTIN, PAN, CIN)</li>
                <li>Product and manufacturing information</li>
                <li>Usage data and analytics for service improvement</li>
              </ul>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Purpose of Processing</h2>
            <div className="text-gray-700 mb-4">
              <p className="mb-2">We process your personal data for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Account registration and verification</li>
                <li>Service provision and platform functionality</li>
                <li>Payment processing and billing</li>
                <li>Compliance with legal and regulatory requirements</li>
                <li>Customer support and communication</li>
                <li>Service improvement and analytics</li>
                <li>Fraud prevention and security</li>
              </ul>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Legal Basis for Processing</h2>
            <p className="text-gray-700 mb-4">
              We process your personal data based on:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
              <li>Your explicit consent for specific processing activities</li>
              <li>Contractual necessity for service provision</li>
              <li>Legal obligations under Indian law</li>
              <li>Legitimate business interests where not overridden by your rights</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Your Rights Under DPDP Act 2023</h2>
            <div className="text-gray-700 mb-4">
              <p className="mb-2">You have the following rights regarding your personal data:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Right to Access:</strong> Request information about personal data being processed</li>
                <li><strong>Right to Correction:</strong> Request correction of inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of personal data in certain circumstances</li>
                <li><strong>Right to Grievance Redressal:</strong> File complaints regarding data processing</li>
                <li><strong>Right to Nominate:</strong> Nominate another person to exercise rights in case of death or incapacity</li>
              </ul>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
            <p className="text-gray-700 mb-4">
              We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, comply with legal obligations, resolve disputes, and enforce our agreements. Specific retention periods vary based on data type and legal requirements.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. This includes encryption, access controls, regular security assessments, and staff training.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Data Sharing</h2>
            <div className="text-gray-700 mb-4">
              <p className="mb-2">We may share your personal data with:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Service providers and business partners who assist in service delivery</li>
                <li>Government authorities when required by law</li>
                <li>Legal advisors and auditors for compliance purposes</li>
                <li>Third parties with your explicit consent</li>
              </ul>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Cross-Border Data Transfer</h2>
            <p className="text-gray-700 mb-4">
              If we transfer your personal data outside India, we ensure appropriate safeguards are in place as required by the DPDP Act, including adequacy decisions or appropriate contractual clauses.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Grievance Redressal</h2>
            <div className="text-gray-700 mb-4">
              <p className="mb-2">For any concerns regarding data processing, you can contact our Data Protection Officer:</p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>Email:</strong> privacy@pureaum.com</p>
                <p><strong>Address:</strong> [Company Address]</p>
                <p><strong>Response Time:</strong> Within 30 days of receipt</p>
              </div>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Updates to This Agreement</h2>
            <p className="text-gray-700 mb-4">
              We may update this Agreement from time to time to reflect changes in our practices or applicable laws. We will notify you of material changes through our platform or via email.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Information</h2>
            <div className="text-gray-700 mb-4">
              <p className="mb-2">For questions about this Agreement or to exercise your rights:</p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p><strong>PureAum Technologies</strong></p>
                <p><strong>Email:</strong> legal@pureaum.com</p>
                <p><strong>Phone:</strong> +91-XXXX-XXXXXX</p>
                <p><strong>Address:</strong> [Complete Business Address]</p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-8">
              <div className="flex">
                <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Important Notice</h3>
                  <div className="text-sm text-yellow-700 mt-1">
                    <p>This Agreement is effective as of the date you accept it during registration. By using our services, you acknowledge that you have read, understood, and agree to be bound by this Agreement.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => window.close()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}